import { useState, useEffect, useCallback } from "react";

import moment from "moment";

import getMetrics from "../api/getMetrics";

import useMetricsContext from "./useMetricsContext";
import useMetricsMaxDuration from "./useMetricsMaxDuration";

/**
 * Time range
 * @typedef {Object} Range
 * @property {string} from - Formatted date
 * @property {string} to - Formatted date
 */

/**
 * @param {number|Range} range a range object or a number to generate a range from.
 *
 * @returns {Range} the range
 */
const getRange = (maxDuration, range) => {
  if (typeof range === "number") {
    const now = moment();

    return {
      to: now.format(),
      from: now.subtract(Math.min(maxDuration, range), "seconds").format()
    };
  }

  return range;
};

/**
 * sets up a polling every 30 seconds based on the query
 *
 * @param {String} chorusURL
 * @param {Object} query
 * @param {number} range in seconds
 * @returns [ { metrics, range }, isLoading, error ]
 */
const useMetrics = (chorusURL, query, range) => {
  const [data, setMetrics] = useState<{ metrics: any[]; range }>({
    metrics: [],
    range
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>();
  const setMetricsError = useMetricsContext()?.setError;
  // Prevent Querying more than the maximum supported range,
  // Fallsback to maxRangeSeconds if rangeInSeconds is undefined
  const maxDuration = useMetricsMaxDuration().seconds;

  const onError = useCallback(
    (error: unknown) => {
      // lets allow errors trickle down for Components that wants
      // to use this hooks but not wrapped in the metrics context like in services
      setError(error);
      setMetricsError?.(error);
    },
    [setMetricsError]
  );

  const load = useCallback(async () => {
    if (!query || !chorusURL) {
      return;
    }
    try {
      const data = await getMetrics(chorusURL, {
        ...query,
        range: getRange(maxDuration, range || maxDuration)
      });
      onError(undefined);
      setMetrics({ metrics: data, range });
    } catch (error) {
      onError(error);
    }
  }, [query, chorusURL, maxDuration, range, onError]);

  useEffect(() => {
    let isCanceled = false;
    let id: number | undefined = -1;
    async function poll() {
      const shouldPoll = typeof range === "number";
      await load();
      if (id && shouldPoll) {
        id = window.setTimeout(poll, 30000);
      }
    }

    (async () => {
      setIsLoading(true);
      await poll();
      if (isCanceled) {
        return;
      }
      setIsLoading(false);
    })();

    return () => {
      isCanceled = true;
      window.clearTimeout(id);
      id = undefined;
    };
  }, [range, query, maxDuration, load]);
  return { data, isLoading, error };
};

export default useMetrics;
